import React from 'react'
import { useUser } from '../hooks/useUser'
import { Navigate } from 'react-router-dom'
import { useFlags } from '../hooks/useFlags'

// Type definitions for guard props
interface AuthGuardProps {
  children: React.ReactNode
  fallback?: string
}

interface RoleGateProps {
  children: React.ReactNode
  role: 'admin'
  fallback?: React.ReactNode | null
}

interface FeatureGateProps {
  children: React.ReactNode
  flag: string
  fallback?: React.ReactNode | null
}

// Authentication Guard - requires user to be logged in
export function AuthGuard({ children, fallback = '/login' }: AuthGuardProps) {
  const { user, isLoading, isAuthenticated } = useUser()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-warning"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to={fallback} replace />
  }

  return <>{children}</>
}

// Role-based Gate - checks user role permissions
export function RoleGate({ children, role, fallback = null }: RoleGateProps) {
  const { hasRole } = useUser()

  // Check if user has required role
  if (!hasRole(role)) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

// Feature Flag Gate - conditionally renders based on feature flags
export function FeatureGate({ children, flag, fallback = null }: FeatureGateProps) {
  const flags = useFlags()
  
  // Check if feature flag is enabled
  const isEnabled = flags && (flags as any)[flag] === true
  
  if (!isEnabled) {
    return <>{fallback}</>
  }
  
  return <>{children}</>
}

// Compound guard that combines multiple conditions
interface GuardProps {
  children: React.ReactNode
  auth?: boolean
  role?: 'admin'
  featureFlag?: string
  visible?: boolean | ((ctx: { isMobile: boolean; flags: Record<string, boolean> }) => boolean)
}

export function Guard({ children, auth, role, featureFlag, visible }: GuardProps) {
  const flags = useFlags()
  const isMobile = window.innerWidth < 768 // Simple mobile detection for visibility function
  
  // Check visibility first
  if (visible !== undefined) {
    if (typeof visible === 'function') {
      if (!visible({ isMobile, flags: flags })) {
        return null
      }
    } else if (!visible) {
      return null
    }
  }
  
  let guardedChildren = children
  
  // Apply feature flag gate if specified
  if (featureFlag) {
    guardedChildren = (
      <FeatureGate flag={featureFlag}>
        {guardedChildren}
      </FeatureGate>
    )
  }
  
  // Apply role gate if specified
  if (role) {
    guardedChildren = (
      <RoleGate role={role}>
        {guardedChildren}
      </RoleGate>
    )
  }
  
  // Apply auth guard if specified
  if (auth) {
    guardedChildren = (
      <AuthGuard>
        {guardedChildren}
      </AuthGuard>
    )
  }
  
  return <>{guardedChildren}</>
}
